import React, { useState, useEffect, useCallback } from 'react';
import { Input } from 'antd';
import type { InputProps as AntdInputProps } from 'antd';

export interface EmailInputProps extends Omit<AntdInputProps, 'onChange' | 'size'> {
  label?: string;
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  onValidationChange?: (isValid: boolean, message: string) => void;
  showValidation?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  required?: boolean;
  helperText?: string;
  customValidator?: (email: string) => { isValid: boolean; message: string };
  fullWidth?: boolean;
  size?: 'small' | 'middle' | 'large';
}

type ValidationState = 'idle' | 'valid' | 'invalid';

export const EmailInput: React.FC<EmailInputProps> = ({
  label,
  value,
  onChange,
  onValidationChange,
  showValidation = true,
  validateOnBlur = true,
  validateOnChange = true,
  required = false,
  helperText,
  customValidator,
  className = '',
  disabled,
  placeholder = 'email@example.com',
  fullWidth = false,
  size = 'large',
  id,
  ...props
}) => {
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState(false);

  // Default email validation
  const defaultValidateEmail = useCallback(
    (email: string): { isValid: boolean; message: string } => {
      if (required && !email) {
        return { isValid: false, message: 'Email is required' };
      }

      if (!email) {
        return { isValid: true, message: '' };
      }

      if (email.length < 3) {
        return { isValid: false, message: 'Email is too short' };
      }

      if (!email.includes('@') || !email.includes('.')) {
        return { isValid: false, message: 'Email must contain @ and domain' };
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { isValid: false, message: 'Please enter a valid email address' };
      }

      return { isValid: true, message: '' };
    },
    [required]
  );

  const validateEmail = customValidator || defaultValidateEmail;

  // Validate email
  const performValidation = useCallback(
    (email: string, updateState: boolean = true) => {
      const validation = validateEmail(email);

      if (updateState) {
        setValidationState(validation.isValid ? 'valid' : 'invalid');
        setErrorMessage(validation.message);
      }

      onValidationChange?.(validation.isValid, validation.message);
      return validation;
    },
    [validateEmail, onValidationChange]
  );

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (validateOnChange && touched) {
        const validation = performValidation(newValue);
        onChange(newValue, validation.isValid);
      } else {
        onChange(newValue, true);
      }
    },
    [validateOnChange, touched, performValidation, onChange]
  );

  // Handle blur
  const handleBlur = useCallback(() => {
    setTouched(true);
    if (validateOnBlur) {
      const validation = performValidation(value);
      onChange(value, validation.isValid);
    }
  }, [validateOnBlur, value, performValidation, onChange]);

  // Validate when value changes externally
  useEffect(() => {
    if (touched && validateOnChange) {
      performValidation(value);
    }
  }, [value, touched, validateOnChange, performValidation]);

  // Get antd status based on validation state
  const getStatus = () => {
    if (!showValidation || validationState === 'idle') return undefined;
    return validationState === 'invalid' ? 'error' : undefined;
  };

  // Get suffix icon based on validation state
  const getSuffixIcon = () => {
    if (!showValidation || validationState === 'idle') return null;

    if (validationState === 'valid') {
      return (
        <svg
          className="h-5 w-5 text-emerald-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      );
    }

    if (validationState === 'invalid') {
      return (
        <svg
          className="h-5 w-5 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    }

    return null;
  };

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className={`block mb-1 text-sm font-medium ${errorMessage && validationState === 'invalid' ? 'text-red-500' : 'text-gray-700'}`} htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <Input
        id={id}
        type="email"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="email"
        status={getStatus()}
        suffix={getSuffixIcon()}
        className={className}
        size={size}
        {...props}
      />

      {showValidation && errorMessage && validationState === 'invalid' && (
        <div className="mt-1 text-xs text-red-500">{errorMessage}</div>
      )}

      {helperText && !errorMessage && (
        <div className="mt-1 text-xs text-gray-500">{helperText}</div>
      )}
    </div>
  );
};

export default EmailInput;
